/**
 * ThemeToggle — cycles between Dark / Light / System themes.
 * Rendered in the TopBar and settings panel.
 */
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useThemeStore, type ThemeMode } from '../../stores/themeStore';
import { fill } from '../../lib/i18nText';

const MODES: { value: ThemeMode; icon: string }[] = [
  { value: 'dark',   icon: '🌙' },
  { value: 'light',  icon: '☀️'  },
  { value: 'system', icon: '💻' },
];

const MODE_LABELS: Record<ThemeMode, string> = {
  dark: 'Dark',
  light: 'Light',
  system: 'System',
};

function modeLabel(t: TFunction, mode: ThemeMode): string {
  return t(`settingsPage.theme.${mode}`, MODE_LABELS[mode]);
}

export function ThemeToggle({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const { mode, setMode } = useThemeStore();

  function cycle() {
    const idx  = MODES.findIndex(m => m.value === mode);
    const next = MODES[(idx + 1) % MODES.length];
    setMode(next.value);
  }

  const current = MODES.find(m => m.value === mode) ?? MODES[0];
  const label = modeLabel(t, current.value);

  return (
    <button
      type="button"
      onClick={cycle}
      title={fill(t('settingsPage.theme.cycleTitle', 'Theme: {{mode}} — click to cycle', { mode: label }), { mode: label })}
      data-testid="theme-toggle"
      className={[
        'flex h-8 w-8 items-center justify-center rounded-md',
        'text-[var(--nord-subtext)] transition-colors',
        'hover:bg-[var(--nord-surface)] hover:text-[var(--nord-text)]',
        className,
      ].join(' ')}
    >
      <span aria-hidden="true" className="text-base leading-none">
        {current.icon}
      </span>
      <span className="sr-only">
        {fill(t('settingsPage.theme.toggleSr', 'Toggle theme ({{mode}})', { mode: label }), { mode: label })}
      </span>
    </button>
  );
}

/** Dropdown variant for settings panels. */
export function ThemeSelect({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const { mode, setMode } = useThemeStore();

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-xs font-medium text-[var(--nord-subtext)] uppercase tracking-wide">
        {t('settings.appearance', 'Appearance')}
      </label>
      <div className="flex gap-1 rounded-lg border border-[var(--nord-border)] p-1 bg-[var(--nord-bg)]">
        {MODES.map(m => (
          <button
            key={m.value}
            type="button"
            onClick={() => setMode(m.value)}
            data-testid={`theme-option-${m.value}`}
            className={[
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm',
              'transition-colors font-medium',
              mode === m.value
                ? 'bg-[var(--nord-surface)] text-[var(--nord-text)] shadow-sm'
                : 'text-[var(--nord-subtext)] hover:text-[var(--nord-text)]',
            ].join(' ')}
          >
            <span aria-hidden="true">{m.icon}</span>
            {modeLabel(t, m.value)}
          </button>
        ))}
      </div>
    </div>
  );
}
